// src/components/CustomerLayout.js
import React, { useState } from "react";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Divider,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  Fade,
  Tooltip,
} from "@mui/material";

import DashboardIcon from "@mui/icons-material/Dashboard";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import WorkIcon from "@mui/icons-material/Work";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import FolderIcon from "@mui/icons-material/Folder";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

import { useCustomerAuth } from "../contexts/CustomerAuthContext";

const drawerWidth = 260;

const LOGO_URL =
  "https://firebasestorage.googleapis.com/v0/b/humbertack-workflow.firebasestorage.app/o/Minimalist%20HumberTrack%20Logo%20Design.png?alt=media&token=7fac08dd-27a3-466a-8172-48d50a827959";

const navItems = [
  { 
    id: "dashboard", 
    label: "Dashboard", 
    icon: <DashboardIcon />,
    description: "Overview"
  },
  { 
    id: "requestJob", 
    label: "Request Job", 
    icon: <AddCircleOutlineIcon />,
    description: "Request new service",
    primary: true
  },
  { 
    id: "jobs", 
    label: "My Jobs", 
    icon: <WorkIcon />,
    description: "View your jobs"
  },
  { 
    id: "invoices", 
    label: "Invoices", 
    icon: <ReceiptIcon />,
    description: "View & pay invoices"
  },
  { 
    id: "vehicles", 
    label: "My Vehicles", 
    icon: <DirectionsCarIcon />,
    description: "Manage your fleet"
  },
  { 
    id: "documents", 
    label: "Documents", 
    icon: <FolderIcon />,
    description: "Certificates & reports"
  },
  { 
    id: "settings", 
    label: "Settings", 
    icon: <SettingsIcon />,
    description: "Account settings"
  },
];

function CustomerLayout({ children, currentPage, onNavigate, onLogout }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const drawerWidthMobile = 200;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountMenu, setAccountMenu] = useState(null);
  const { customer } = useCustomerAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (pageId) => {
    onNavigate(pageId);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleAccountMenuOpen = (event) => {
    setAccountMenu(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAccountMenu(null);
  };

  const handleLogout = () => {
    handleAccountMenuClose();
    onLogout();
  };

  // Sidebar content
  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: { xs: 1, sm: 2 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            component="img"
            src={LOGO_URL}
            alt="HumberTrack"
            sx={{
              width: { xs: 28, sm: 40 },
              height: { xs: 28, sm: 40 },
              objectFit: "contain",
            }}
          />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: { xs: '1rem', sm: '1.15rem' } }}>
              HumberTrack
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Customer Portal
            </Typography>
          </Box>
        </Box>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Navigation Items */}
      <List sx={{ flexGrow: 1, px: 1, py: 2 }}>
        {navItems.map((item) => {
          const isActive = currentPage === item.id;
          const isPrimary = item.primary;

          return (
            <ListItemButton
              key={item.id}
              onClick={() => handleNavigation(item.id)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                py: 1.5,
                px: 2,
                transition: "all 0.2s ease",
                bgcolor: isActive
                  ? isPrimary
                    ? "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)"
                    : "rgba(245,158,11,0.1)"
                  : "transparent",
                background: isActive && isPrimary
                  ? "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)"
                  : undefined,
                color: isActive && isPrimary ? "white" : "inherit",
                "&:hover": {
                  bgcolor: isActive
                    ? isPrimary
                      ? "linear-gradient(135deg, #d97706 0%, #dc2626 100%)"
                      : "rgba(245,158,11,0.15)"
                    : "rgba(245,158,11,0.05)",
                  background: isActive && isPrimary
                    ? "linear-gradient(135deg, #d97706 0%, #dc2626 100%)"
                    : undefined,
                  transform: "translateX(4px)",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: isActive && isPrimary ? "white" : "#f59e0b",
                  minWidth: 40,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                secondary={!isActive && item.description}
                secondaryTypographyProps={{
                  sx: { fontSize: "0.7rem", opacity: 0.7 },
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Divider />

      {/* User Profile */}
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 1.5,
            borderRadius: 2,
            bgcolor: "rgba(245,158,11,0.05)",
            border: "1px solid rgba(245,158,11,0.2)",
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: "rgba(245,158,11,0.1)",
              transform: "scale(1.02)",
            },
          }}
          onClick={handleAccountMenuOpen}
        >
          <Avatar
            sx={{
              bgcolor: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
              background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
              width: 40,
              height: 40,
            }}
          >
            <AccountCircleIcon />
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
              {customer?.name || "Customer"}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {customer?.email}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* App Bar - Mobile Only */}
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
            zIndex: theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 48, sm: 56 } }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontWeight: 700, fontSize: { xs: '1rem', sm: '1.15rem' } }}>
              HumberTrack
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Sidebar - Desktop */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Sidebar - Mobile */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            "& .MuiDrawer-paper": {
              width: drawerWidthMobile,
              boxSizing: "border-box",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: isMobile ? 7 : 0,
          minHeight: "100vh",
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          {children}
        </Box>
      </Box>

      {/* Account Menu */}
      <Menu
        anchorEl={accountMenu}
        open={Boolean(accountMenu)}
        onClose={handleAccountMenuClose}
        TransitionComponent={Fade}
      >
        <MenuItem onClick={() => {
          handleAccountMenuClose();
          handleNavigation('settings');
        }}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          Account Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default CustomerLayout;